//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { ExistsWithNetworkItem } from '@models/INetworkItem';
import { SocialNetwork, getSocialNetworkName } from '@models/SocialNetwork';

const MAX_TITLE_LENGTH = 100;

export const formatTikTokEmbedTitle = (
  item: ExistsWithNetworkItem<IMlsMediaItem>,
): string => formatEmbedTitle(item, SocialNetwork.TikTok);

export const formatYouTubeEmbedTitle = (
  item: ExistsWithNetworkItem<IMlsMediaItem>,
): string => formatEmbedTitle(item, SocialNetwork.YouTube);

/**
 * Formats a title to be used for an embed
 *
 * @example - JohnDoe's $network post of blue shirt, brown shoes, and jeans
 * @example - JohnDoe's $network post of blue shirt and brown shoes
 * @example - JohnDoe's $network post of blue shirt
 * @example - $network post of blue shirt, brown shoes, and jeans
 * @example - $network post
 */
const formatEmbedTitle = (
  item: ExistsWithNetworkItem<IMlsMediaItem>,
  network: SocialNetwork,
): string => {
  const caption = item.networkItem.caption;

  const userText = item.networkUser?.username
    ? `${item.networkUser.username}'s`
    : undefined;

  const productNames =
    item.productTags
      ?.map(({ metadata }) => metadata?.name)
      .filter((name) => !!name) ?? [];

  const productText = (() => {
    if (productNames.length > 2) {
      return `${productNames
        .slice(0, productNames.length - 1)
        .join(', ')} and ${productNames.pop()}`;
    }

    if (productNames.length === 2) {
      return `${productNames[0]} and ${productNames[1]}`;
    }

    if (productNames.length === 1) {
      return `${productNames[0]}`;
    }

    return undefined;
  })();

  const endPart = (() => {
    if (productText) {
      return `of ${productText}`;
    }

    if (caption) {
      return `of ${caption}`;
    }

    return undefined;
  })();

  productText ? `of ${productText}` : caption ? `of ${caption}` : undefined;
  const title = [userText, `${getSocialNetworkName(network)} post`, endPart]
    .filter((part) => !!part)
    .join(' ');

  return title.length > MAX_TITLE_LENGTH
    ? `${title.slice(0, MAX_TITLE_LENGTH - 3)}...`
    : title;
};
